import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import {
  EXPERT_ONLY_NOTIFICATION_SECTION_TYPES,
  NOTIFICATION_SETTINGS_COPY,
  NOTIFICATION_SETTINGS_SECTIONS,
} from '@/constants/notificationSettings';
import { useToaster } from '@/hooks/useToaster';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import {
  NotificationPreferenceBodyV2,
  setNotificationPreferenceV2,
} from '@/services/setNotificationPreferenceV2';
import { trackOsNotificationPermissionSet, type PushOptInSource } from '@/util/analyticsEvents';
import { requestPushNotificationPermissions, waitForPushReady } from '@/util/pushNotifications';
import { queryClient } from '@/util/queryClient';
import { Sentry } from '@/util/sentry';

/**
 * Shared push opt-in: request the OS permission and, on grant, enable push for
 * the notification events available to this user's type, then invalidate the
 * user query so any mounted notification-settings screen reflects the new
 * prefs without a reload. Returns whether permission ended up granted.
 *
 * Single source of truth for the opt-in so the app-level permission modal and
 * the in-screen "Turn on" Alert button behave identically — including the
 * user-type split (experts also get the inquiry/reference events submitters
 * never receive). Push prefs default off server-side, so writing them on
 * opt-in is required; deriving the set from NOTIFICATION_SETTINGS_SECTIONS
 * (not `event: 'all'`) avoids writing expert-only prefs for submitters.
 *
 * Reports the permission outcome either way. The dialog is the only place the
 * grant is won or lost, so tracking it here means every push surface is
 * measured by calling this hook, and `source` is what separates them.
 *
 * On grant, confirms with a success toast (parallel to the SMS opt-in's
 * "Text notifications on") — the triggering UI often disappears, so the toast
 * is the user's only confirmation it worked.
 */
export function useEnablePush(source: PushOptInSource) {
  const userId = useAtomValue(userIdAtom);

  const { data } = useQuery(getCurrentUserV2QueryOptions(userId));

  const toast = useToaster();

  return useCallback(async () => {
    /*
     * Checked before asking because a provider SDK that never initialized
     * leaves the permission call unsettled forever, which would strand this
     * hook mid-await and report neither outcome.
     */
    if (!(await waitForPushReady())) {
      trackOsNotificationPermissionSet({
        response: 'unavailable',
        source,
        userType: data?.userType,
      });

      Sentry.captureException(new Error('Push opt-in unavailable: OneSignal did not initialize'), {
        tags: { component: 'useEnablePush', source: 'push-notifications' },
        extra: { optInSource: source, userType: data?.userType },
      });

      toast.showError({
        title: NOTIFICATION_SETTINGS_COPY.pushUnavailableTitle,
        message: NOTIFICATION_SETTINGS_COPY.pushUnavailableMessage,
      });

      return false;
    }

    const granted = await requestPushNotificationPermissions();

    trackOsNotificationPermissionSet({
      response: granted ? 'allowed' : 'declined',
      source,
      userType: data?.userType,
    });

    if (granted) {
      const isExpert = data?.userType === 'expert';

      const availablePushEvents = NOTIFICATION_SETTINGS_SECTIONS.map(
        (section) => section.type
      ).filter((type) => isExpert || !EXPERT_ONLY_NOTIFICATION_SECTION_TYPES.includes(type));

      await Promise.all(
        availablePushEvents.map((event) =>
          setNotificationPreferenceV2({
            channel: 'push',
            event: event as NotificationPreferenceBodyV2['event'],
            enabled: true,
          })
        )
      );

      queryClient.invalidateQueries({
        queryKey: getCurrentUserV2QueryOptions(userId).queryKey,
      });

      toast.showSuccess({ title: 'Push notifications on' });
    }

    return granted;
  }, [data, userId, toast, source]);
}
